import React, { useState, useEffect, useMemo } from 'react';
import { 
  Container, Typography, Box, Paper, Button, CircularProgress, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Snackbar, Alert, Chip, Switch, FormControlLabel
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import { api, UserProfile } from '../services/api';
import { Idea } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { ADMIN_EMAILS } from '../constants';
import { formatDate } from '../utils/formatDate';
import { mergeUsersByEmail, normalizeEmail } from '../utils/mergeUsers';

type UserSortKey = 'email' | 'displayName' | 'connection' | 'createdAt' | 'ideasCount' | 'commentsCount' | 'likesCount';

const AdminPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { tenantConfig } = useTenant();
  const navigate = useNavigate();
  const [allIdeas, setAllIdeas] = useState<Idea[]>([]);
  const [filterApproved, setFilterApproved] = useState(true);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [userOrderBy, setUserOrderBy] = useState<UserSortKey>('createdAt');
  const [userOrder, setUserOrder] = useState<'asc' | 'desc'>('desc');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Check if user is admin
  const isAdmin = currentUser?.email && ADMIN_EMAILS.includes(currentUser.email);

  useEffect(() => {
    // Redirect if not admin
    if (!isAdmin && !loading) {
      navigate('/');
    }
  }, [isAdmin, loading, navigate]);

  // Fetch all ideas
  useEffect(() => {
    const fetchIdeas = async () => {
      if (!isAdmin) return;
      
      try {
        setLoading(true);
        // Fetch all ideas
        const fetchedIdeas = await api.getIdeas('mostRecent');
        setAllIdeas(fetchedIdeas);
      } catch (err) {
        console.error('Failed to fetch ideas:', err);
        setError('Failed to load ideas. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchIdeas();
  }, [isAdmin]);

  // Fetch all users to get emails and additional data
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAdmin) return;
      
      try {
        setUsersLoading(true);
        // Fetch all users
        const users = await api.getUsers();
        setAllUsers(users);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [isAdmin]);

  // Duplicate accounts sharing an email collapse into one record
  const mergedUsers = useMemo(() => mergeUsersByEmail(allUsers), [allUsers]);

  // Every raw database uuid resolves to its merged record so idea rows show merged data
  const userByUuid = useMemo(() => {
    const byEmail = new Map(mergedUsers.map(user => [normalizeEmail(user.email), user]));
    const map: Record<string, UserProfile> = {};
    allUsers.forEach(user => {
      if (user.database_uuid) {
        map[user.database_uuid] = byEmail.get(normalizeEmail(user.email)) || user;
      }
    });
    return map;
  }, [allUsers, mergedUsers]);

  const sortedUsers = useMemo(() => {
    const value = (user: UserProfile): string | number => {
      switch (userOrderBy) {
        case 'email': return (user.email || '').toLowerCase();
        case 'displayName': return (user.displayName || '').toLowerCase();
        case 'connection': return (user.connection || '').toLowerCase();
        case 'createdAt': return user.createdAt ? user.createdAt.getTime() : 0;
        case 'ideasCount': return user.ideasCount ?? 0;
        case 'commentsCount': return user.commentsCount ?? 0;
        case 'likesCount': return user.likesCount ?? 0;
      }
    };
    const direction = userOrder === 'asc' ? 1 : -1;
    return [...mergedUsers].sort((a, b) => {
      const aValue = value(a);
      const bValue = value(b);
      if (aValue < bValue) return -direction;
      if (aValue > bValue) return direction;
      return 0;
    });
  }, [mergedUsers, userOrderBy, userOrder]);

  const handleUserSort = (key: UserSortKey) => {
    if (userOrderBy === key) {
      setUserOrder(userOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setUserOrderBy(key);
      setUserOrder('asc');
    }
  };

  const handleApprove = async (ideaId: string) => {
    try {
      await api.approveIdea(ideaId, true);
      // Update the idea in the list
      setAllIdeas(prev => 
        prev.map(idea => 
          idea.id === ideaId ? { ...idea, approved: true } : idea
        )
      );
      setSnackbar({
        open: true,
        message: 'Idea approved successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to approve idea:', err);
      setSnackbar({
        open: true,
        message: 'Failed to approve idea',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterApproved(event.target.checked);
  };

  // Export users to CSV
  const handleExportUsers = () => {
    if (mergedUsers.length === 0) {
      setSnackbar({
        open: true,
        message: 'No users to export',
        severity: 'error'
      });
      return;
    }

    // Demographic columns are included for every tenant (empty when not collected)
    const headers = [
      'Email', 'Display Name', 'Connection', 'Zip Code', 'Ward', 'Address', 'Race/Ethnicity',
      'Signed Up', 'Ideas', 'Comments', 'Likes',
    ];

    // Helper to escape CSV values
    const escapeCSV = (value: string | undefined): string => {
      if (!value) return '';
      // If the value contains comma, quote, or newline, wrap in quotes and escape inner quotes
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Build CSV rows in the currently displayed sort order
    const rows = sortedUsers.map(user => [
      escapeCSV(user.email),
      escapeCSV(user.displayName),
      escapeCSV(user.connection),
      escapeCSV(user.zip_code),
      escapeCSV(user.ward),
      escapeCSV(user.address),
      escapeCSV(user.race_ethnicity),
      escapeCSV(user.createdAt ? user.createdAt.toISOString().split('T')[0] : ''),
      String(user.ideasCount ?? 0),
      String(user.commentsCount ?? 0),
      String(user.likesCount ?? 0),
    ].join(','));

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const tenantName = tenantConfig?.tenant_id || 'tenant';
    const timestamp = new Date().toISOString().split('T')[0];
    link.download = `${tenantName}-users-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: `Exported ${mergedUsers.length} users to CSV`,
      severity: 'success'
    });
  };

  // Get email for a user by their ID
  const getUserEmail = (userId: string | undefined): string => {
    if (!userId) return 'N/A';
    return userByUuid[userId]?.email || 'Email not found';
  };

  // Get zip code for a user by their ID
  const getUserZipCode = (userId: string | undefined): string => {
    if (!userId) return 'N/A';
    return userByUuid[userId]?.zip_code || 'N/A';
  };

  // Get ward for a user by their ID
  const getUserWard = (userId: string | undefined): string => {
    if (!userId) return 'N/A';
    return userByUuid[userId]?.ward || 'N/A';
  };

  // Get address for a user by their ID
  const getUserAddress = (userId: string | undefined): string => {
    if (!userId) return 'N/A';
    return userByUuid[userId]?.address || 'N/A';
  };

  // Get race/ethnicity for a user by their ID
  const getUserRaceEthnicity = (userId: string | undefined): string => {
    if (!userId) return 'N/A';
    return userByUuid[userId]?.race_ethnicity || 'N/A';
  };

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Container>
    );
  }

  if (loading || usersLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading data...</Typography>
      </Container>
    );
  }

  // Filter ideas based on the toggle state
  const displayedIdeas = filterApproved 
    ? allIdeas.filter(idea => !idea.approved) 
    : allIdeas;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        Admin Panel - Idea Approval
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={filterApproved} onChange={handleFilterChange} />}
          label="Show Only Unapproved Ideas"
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportUsers}
          disabled={usersLoading || mergedUsers.length === 0}
        >
          Export Users ({mergedUsers.length})
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {displayedIdeas.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>{filterApproved ? 'No ideas waiting for approval' : 'No ideas found'}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
                <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                {tenantConfig?.tenant_id === 'evanston' && (
                  <TableCell>Anonymous</TableCell>
                )}
                <TableCell>Email</TableCell>
                {tenantConfig?.tenant_id === 'evanston' && (
                  <>
                    <TableCell>Zip Code</TableCell>
                    <TableCell>Ward</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Race/Ethnicity</TableCell>
                  </>
                )}
                <TableCell>Submitted</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedIdeas.map((idea) => (
                <TableRow key={idea.id}>
                  <TableCell>
                    <Button 
                      color="primary"
                      // Use template literal for correct navigation
                      onClick={() => navigate(`/idea/${idea.id}`)}
                    >
                      {idea.title}
                    </Button>
                  </TableCell>
                  {/* Use user_display_name for author - for anonymous posts, still show real name to admins */}
                  <TableCell>
                    {idea.user_display_name || 'N/A'}
                  </TableCell>
                  {tenantConfig?.tenant_id === 'evanston' && (
                    <TableCell>
                      {idea.is_anonymous ? (
                        <Chip label="Yes" color="info" size="small" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">No</Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell>{getUserEmail(idea.user_id)}</TableCell>
                  {tenantConfig?.tenant_id === 'evanston' && (
                    <>
                      <TableCell>{getUserZipCode(idea.user_id)}</TableCell>
                      <TableCell>{getUserWard(idea.user_id)}</TableCell>
                      <TableCell>{getUserAddress(idea.user_id)}</TableCell>
                      <TableCell>{getUserRaceEthnicity(idea.user_id)}</TableCell>
                    </>
                  )}
                  <TableCell>{formatDate(idea.created_at)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {idea.tags?.map((tag) => (
                        <Chip key={tag.id} label={tag.name} size="small" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={idea.approved ? 'Approved' : 'Pending'} 
                      color={idea.approved ? 'success' : 'warning'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleApprove(idea.id)}
                      // Disable button if idea is already approved
                      disabled={idea.approved}
                      sx={{ mr: 1 }}
                    >
                      Approve
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        Users ({mergedUsers.length})
      </Typography>

      {mergedUsers.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No users found</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 4, overflowX: 'auto' }} data-testid="users-table">
          <Table>
            <TableHead>
              <TableRow>
                {([
                  ['email', 'Email', 'left'],
                  ['displayName', 'Display Name', 'left'],
                  ['connection', 'Connection', 'left'],
                  ['createdAt', 'Signed Up', 'left'],
                  ['ideasCount', 'Ideas', 'right'],
                  ['commentsCount', 'Comments', 'right'],
                  ['likesCount', 'Likes', 'right'],
                ] as [UserSortKey, string, 'left' | 'right'][]).map(([key, label, align]) => (
                  <TableCell
                    key={key}
                    align={align}
                    sortDirection={userOrderBy === key ? userOrder : false}
                  >
                    <TableSortLabel
                      active={userOrderBy === key}
                      direction={userOrderBy === key ? userOrder : 'asc'}
                      onClick={() => handleUserSort(key)}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.database_uuid || user.uid}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.displayName}</TableCell>
                  <TableCell>{user.connection || 'N/A'}</TableCell>
                  <TableCell>{user.createdAt ? formatDate(user.createdAt) : 'N/A'}</TableCell>
                  <TableCell align="right">{user.ideasCount ?? 0}</TableCell>
                  <TableCell align="right">{user.commentsCount ?? 0}</TableCell>
                  <TableCell align="right">{user.likesCount ?? 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminPage; 